import React, { useState, useMemo } from 'react';
import {
  Wrench,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Shield,
  ShieldCheck,
  ShieldAlert,
  CreditCard,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  Star,
  ExternalLink,
  Ban,
  RotateCcw,
  Eye,
  SlidersHorizontal,
} from 'lucide-react';
import { VerificationBadge } from '../common/VerificationBadge';
import type { VerificationStatus } from '../../types';

interface AdminProfessionalSectionProps {
  professionals: any[];
  onAction: (
    professionalId: string,
    action: 'verify' | 'reject' | 'suspend' | 'reactivate',
    reason?: string
  ) => Promise<void>;
  isProcessing: boolean;
}

export const AdminProfessionalSection: React.FC<AdminProfessionalSectionProps> = ({
  professionals,
  onAction,
  isProcessing,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [selectedPro, setSelectedPro] = useState<any | null>(null);
  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    pro: any | null;
    action: 'verify' | 'reject' | 'suspend' | 'reactivate';
  }>({
    isOpen: false,
    pro: null,
    action: 'verify',
  });
  const [actionReason, setActionReason] = useState('');

  const categories = useMemo(() => {
    const set = new Set<string>();
    professionals.forEach((p) => {
      if (p.primary_category || p.primaryCategory) {
        set.add(p.primary_category || p.primaryCategory);
      }
    });
    return Array.from(set);
  }, [professionals]);

  const filteredPros = useMemo(() => {
    return professionals.filter((p) => {
      const name = (p.full_name || p.name || '').toLowerCase();
      const business = (p.business_name || p.businessName || '').toLowerCase();
      const phone = p.phone || '';
      const email = (p.email || '').toLowerCase();
      const cat = (p.primary_category || p.primaryCategory || '').toLowerCase();
      const st = (p.state || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm ||
        name.includes(term) ||
        business.includes(term) ||
        phone.includes(term) ||
        email.includes(term) ||
        p.id.toLowerCase().includes(term);

      const matchesCategory =
        categoryFilter === 'all' || cat === categoryFilter.toLowerCase();

      const matchesState = stateFilter === 'all' || st === stateFilter.toLowerCase();

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'suspended' && p.isSuspended) ||
        (statusFilter === 'active' && !p.isSuspended && (p.verification_status || p.verificationStatus) === 'verified') ||
        (statusFilter === 'pending' && (p.verification_status || p.verificationStatus) === 'pending') ||
        (statusFilter === 'rejected' && (p.verification_status || p.verificationStatus) === 'rejected');

      const matchesSub =
        subFilter === 'all' ||
        (subFilter === 'active' && (p.subscriptionStatus === 'active' || p.subscription_status === 'active')) ||
        (subFilter === 'expired' && (p.subscriptionStatus !== 'active' && p.subscription_status !== 'active'));

      return matchesSearch && matchesCategory && matchesState && matchesStatus && matchesSub;
    });
  }, [professionals, searchTerm, categoryFilter, stateFilter, statusFilter, subFilter]);

  const handleOpenActionModal = (
    pro: any,
    action: 'verify' | 'reject' | 'suspend' | 'reactivate'
  ) => {
    setActionModal({ isOpen: true, pro, action });
    setActionReason(
      action === 'verify'
        ? 'Government ID and Trade Test credentials verified by administrative audit.'
        : action === 'reject'
        ? 'Submitted identification documents could not be verified against NIMC database.'
        : action === 'suspend'
        ? 'Administrative suspension pending investigation.'
        : 'Reactivated following verification.'
    );
  };

  const handleConfirmAction = async () => {
    if (!actionModal.pro) return;
    await onAction(actionModal.pro.id, actionModal.action, actionReason);
    setActionModal({ isOpen: false, pro: null, action: 'verify' });
    if (selectedPro?.id === actionModal.pro.id) {
      setSelectedPro(null);
    }
  };

  return (
    <div id="admin-professionals-section" className="space-y-6 animate-fade-in">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 rounded-2xl">
        <div>
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Wrench className="w-5 h-5 text-emerald-400" />
            <span>Artisans & Professionals Roster ({professionals.length})</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Manage artisan verification audits, marketplace trades, and account authorizations.
          </p>
        </div>

        {/* Filter Badges Summary */}
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
            {professionals.filter((p) => (p.verification_status || p.verificationStatus) === 'verified').length} Verified
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/20 font-semibold">
            {professionals.filter((p) => (p.verification_status || p.verificationStatus) === 'pending').length} Pending
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20 font-semibold">
            {professionals.length} Active Marketplace Pros
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Search */}
        <div className="relative lg:col-span-2">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, business, phone, email, or ID..."
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </div>

        {/* Category */}
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-emerald-500"
          >
            <option value="all">All Trade Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Verification / Account Status */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-emerald-500"
          >
            <option value="all">All Verification States</option>
            <option value="active">Verified Active</option>
            <option value="pending">Pending ID Audit</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Marketplace Access Filter */}
        <div>
          <select
            value={subFilter}
            onChange={(e) => setSubFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-emerald-500"
          >
            <option value="all">All Marketplace Artisans</option>
            <option value="active">Active Marketplace Pros</option>
          </select>
        </div>
      </div>

      {/* Artisans Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/60 border-b border-slate-800 text-[11px] uppercase font-bold text-slate-400 tracking-wider">
              <tr>
                <th className="py-3.5 px-4">Artisan & Business</th>
                <th className="py-3.5 px-4">Trade & Location</th>
                <th className="py-3.5 px-4">Verification Audit</th>
                <th className="py-3.5 px-4">Marketplace Access</th>
                <th className="py-3.5 px-4">Jobs & Rating</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredPros.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    No professionals found matching the selected filters.
                  </td>
                </tr>
              ) : (
                filteredPros.map((pro) => {
                  const verStatus: VerificationStatus =
                    pro.verification_status || pro.verificationStatus || 'pending';
                  const isSubActive =
                    pro.subscriptionStatus === 'active' || pro.subscription_status === 'active';
                  const isSuspended = Boolean(pro.isSuspended);

                  return (
                    <tr
                      key={pro.id}
                      className={`hover:bg-slate-800/40 transition-colors ${
                        isSuspended ? 'bg-rose-950/10' : ''
                      }`}
                    >
                      {/* Name & Contact */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-slate-200 text-xs flex-shrink-0">
                            {pro.full_name?.charAt(0) || 'P'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-white">{pro.full_name}</span>
                              {isSuspended && (
                                <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                  SUSPENDED
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                              <span>{pro.business_name || 'Individual Artisan'}</span>
                              <span>•</span>
                              <span className="font-mono">{pro.phone}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Trade & Location */}
                      <td className="py-3.5 px-4">
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 font-semibold capitalize text-[11px] border border-emerald-500/20">
                            {pro.primary_category || pro.primaryCategory || 'Artisan'}
                          </span>
                          <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3 text-slate-500" />
                            <span>{pro.lga ? `${pro.lga}, ${pro.state}` : pro.state || 'Lagos'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Verification Status */}
                      <td className="py-3.5 px-4">
                        <VerificationBadge status={verStatus} />
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">
                          ID: {pro.idType || 'NIN'}
                        </div>
                      </td>

                      {/* Marketplace Access */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            <CreditCard className="w-3 h-3" />
                            <span>Free / 10% Comm</span>
                          </span>
                        </div>
                      </td>

                      {/* Jobs & Rating */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-amber-400 font-semibold text-xs">
                            <Star className="w-3.5 h-3.5 fill-amber-400" />
                            <span>{(pro.rating || 5.0).toFixed(1)}</span>
                            <span className="text-slate-500 text-[10px]">
                              ({pro.review_count || 12} reviews)
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {pro.completedJobsCount || pro.completed_jobs_count || 12} completed
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View Profile */}
                          <button
                            onClick={() => setSelectedPro(pro)}
                            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                            title="Inspect Full Profile & Documents"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          {/* Verify */}
                          {verStatus !== 'verified' && !isSuspended && (
                            <button
                              onClick={() => handleOpenActionModal(pro, 'verify')}
                              className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-300 hover:text-slate-950 rounded-lg text-xs font-bold border border-emerald-500/30 transition-all"
                            >
                              Verify
                            </button>
                          )}

                          {/* Reject */}
                          {verStatus === 'pending' && !isSuspended && (
                            <button
                              onClick={() => handleOpenActionModal(pro, 'reject')}
                              className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500 text-rose-300 hover:text-white rounded-lg text-xs font-bold border border-rose-500/30 transition-all"
                            >
                              Reject
                            </button>
                          )}

                          {/* Suspend / Reactivate */}
                          {isSuspended ? (
                            <button
                              onClick={() => handleOpenActionModal(pro, 'reactivate')}
                              className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500 text-blue-300 hover:text-white rounded-lg text-xs font-bold border border-blue-500/30 transition-all"
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleOpenActionModal(pro, 'suspend')}
                              className="p-1.5 bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 rounded-lg transition-colors"
                              title="Suspend Artisan"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full Profile Inspector Modal */}
      {selectedPro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center font-bold text-xl">
                  {selectedPro.full_name?.charAt(0) || 'P'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{selectedPro.full_name}</h3>
                    <VerificationBadge
                      status={selectedPro.verification_status || selectedPro.verificationStatus}
                    />
                  </div>
                  <p className="text-xs text-slate-400">
                    {selectedPro.business_name || 'Artisan Service'} • ID: {selectedPro.id}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setSelectedPro(null)}
                className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Verification Credentials Card */}
            <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" />
                <span>Government & Trade Verification Documents</span>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500">Document Type:</span>
                  <p className="text-slate-200 font-medium">{selectedPro.idType || 'National Identity Number (NIN)'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Identification Number:</span>
                  <p className="text-slate-200 font-mono font-medium">{selectedPro.idNumber || '29384756102'}</p>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500">Trade Certifications:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(selectedPro.tradeCertifications || ['Trade Test Grade 1 - Plumbing & Mechanical Services']).map(
                      (cert: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-[11px]"
                        >
                          {cert}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Performance & Subscription Stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-800/50 border border-slate-700/60 p-3 rounded-xl">
                <div className="text-[11px] text-slate-400">Completed Jobs</div>
                <div className="text-base font-bold text-white mt-0.5">
                  {selectedPro.completedJobsCount || selectedPro.completed_jobs_count || 12}
                </div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/60 p-3 rounded-xl">
                <div className="text-[11px] text-slate-400">Avg Rating</div>
                <div className="text-base font-bold text-amber-400 mt-0.5 flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400" />
                  <span>{(selectedPro.rating || 5.0).toFixed(1)}</span>
                </div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/60 p-3 rounded-xl">
                <div className="text-[11px] text-slate-400">Marketplace Model</div>
                <div className="text-base font-bold text-emerald-400 mt-0.5 font-mono">
                  10% Comm
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                onClick={() => setSelectedPro(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Close
              </button>
              {(selectedPro.verification_status || selectedPro.verificationStatus) !== 'verified' && (
                <button
                  onClick={() => handleOpenActionModal(selectedPro, 'verify')}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-bold rounded-xl"
                >
                  Approve Verification
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation & Reason Action Modal */}
      {actionModal.isOpen && actionModal.pro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-bold text-white capitalize flex items-center gap-2">
              {actionModal.action === 'suspend' && <Ban className="w-5 h-5 text-rose-400" />}
              {actionModal.action === 'verify' && <ShieldCheck className="w-5 h-5 text-emerald-400" />}
              {actionModal.action === 'reject' && <XCircle className="w-5 h-5 text-rose-400" />}
              {actionModal.action === 'reactivate' && <RotateCcw className="w-5 h-5 text-blue-400" />}
              <span>{actionModal.action === 'verify' ? 'Verify' : actionModal.action} Artisan: {actionModal.pro.full_name}</span>
            </h3>

            <p className="text-xs text-slate-300">
              {actionModal.action === 'suspend' &&
                'Suspension blocks this professional from submitting quotes and bidding on jobs. This will be recorded in the audit log.'}
              {actionModal.action === 'verify' &&
                'Approving verification grants the verified badge and full quoting access. Professional will receive a confirmation notification.'}
              {actionModal.action === 'reject' &&
                'Rejecting verification will notify the artisan with the reason provided below.'}
              {actionModal.action === 'reactivate' &&
                'Reactivating account restores full artisan quoting permissions.'}
            </p>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Reason / Administrative Note:
              </label>
              <textarea
                rows={3}
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter justification for this action..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setActionModal({ isOpen: false, pro: null, action: 'verify' })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={isProcessing || !actionReason.trim()}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition-colors disabled:opacity-50 cursor-pointer ${
                  actionModal.action === 'suspend' || actionModal.action === 'reject'
                    ? 'bg-rose-500 hover:bg-rose-600 text-white'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-slate-950'
                }`}
              >
                {isProcessing
                  ? 'Processing...'
                  : actionModal.action === 'verify'
                  ? 'Approve Verification'
                  : `Confirm ${actionModal.action.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
